<template>
  <el-dialog
    title="入住批量导入 / 导出"
    :visible.sync="dialogVisible"
    width="720px"
    top="8vh"
    append-to-body
    custom-class="dorm-live-batch-dialog"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-tabs v-model="activeTab" class="batch-tabs">
      <el-tab-pane name="import">
        <span slot="label"><i class="el-icon-upload2" /> 批量导入</span>
      </el-tab-pane>
      <el-tab-pane name="export">
        <span slot="label"><i class="el-icon-download" /> 批量导出</span>
      </el-tab-pane>
    </el-tabs>

    <!-- 导入 -->
    <div v-show="activeTab === 'import'" class="pane">
      <div class="step-nav">
        <div
          v-for="(s, i) in importSteps"
          :key="s"
          class="step-nav-item"
          :class="{ active: importStep === i, done: importStep > i }"
        >
          <span class="step-num">{{ i + 1 }}</span>
          <span class="step-label">{{ s }}</span>
        </div>
      </div>

      <div v-if="importStep === 0" class="flow">
        <div class="flow-card">
          <div class="flow-icon"><i class="el-icon-document" /></div>
          <div class="flow-body">
            <div class="flow-title">下载导入模板</div>
            <p class="flow-desc">按模板填写学号、姓名、宿舍楼、房间号、床位号</p>
            <el-button size="small" plain icon="el-icon-download" @click="downloadTemplate">下载模板</el-button>
          </div>
        </div>
        <div class="flow-card primary">
          <div class="flow-icon"><i class="el-icon-folder-opened" /></div>
          <div class="flow-body">
            <div class="flow-title">上传已填表格</div>
            <p class="flow-desc">支持 .xlsx / .xls，上传后进入预览核对</p>
            <el-upload
              action=""
              :auto-upload="false"
              :show-file-list="false"
              accept=".xlsx,.xls"
              :on-change="onFileChange"
            >
              <el-button type="primary" size="small" icon="el-icon-upload2" :loading="parsing">
                选择文件并解析
              </el-button>
            </el-upload>
          </div>
        </div>
      </div>

      <div v-else-if="importStep === 1" class="flow">
        <div class="preview-meta">
          共解析 <b>{{ previewRows.length }}</b> 行，可剔除无效行后确认导入
        </div>
        <el-table :data="previewRows" border size="mini" max-height="340" class="preview-table">
          <el-table-column type="index" width="50" label="#" />
          <el-table-column prop="StuCode" label="学号" min-width="120" show-overflow-tooltip />
          <el-table-column prop="StuName" label="姓名" width="90" />
          <el-table-column prop="BuildingName" label="宿舍楼" min-width="110" show-overflow-tooltip />
          <el-table-column prop="RoomCode" label="房间号" width="90" />
          <el-table-column prop="BedNumber" label="床位号" width="80" align="center" />
          <el-table-column label="操作" width="90" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="mini" class="danger" @click="removePreviewRow(scope.$index)">剔除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="preview-actions">
          <el-button size="small" @click="importStep = 0">重新上传</el-button>
          <el-button type="primary" size="small" :loading="importing" :disabled="!previewRows.length" @click="confirmImport">
            确认导入 {{ previewRows.length }} 条
          </el-button>
        </div>
      </div>

      <div v-else class="flow">
        <div class="result-banner" :class="importResult.FailCount ? 'is-warn' : 'is-ok'">
          <i :class="importResult.FailCount ? 'el-icon-warning' : 'el-icon-success'" />
          <div>
            <div class="result-title">{{ importResultTitle }}</div>
            <div class="result-sub">{{ importResultSub }}</div>
          </div>
        </div>
        <div v-if="importLogRows.length" class="result-toolbar">
          <el-input
            v-model="resultKeyword"
            clearable
            size="small"
            prefix-icon="el-icon-search"
            placeholder="搜索行号 / 内容"
            style="width: 220px"
          />
          <el-radio-group v-model="resultStatusFilter" size="mini">
            <el-radio-button label="">全部 {{ importLogRows.length }}</el-radio-button>
            <el-radio-button label="success">成功 {{ importSuccessCount }}</el-radio-button>
            <el-radio-button label="fail">失败 {{ importFailCount }}</el-radio-button>
          </el-radio-group>
        </div>
        <el-table
          v-if="importLogRows.length"
          :data="filteredImportLogRows"
          border
          size="mini"
          max-height="320"
          class="preview-table"
        >
          <el-table-column prop="rowNo" label="行号" width="70" align="center" />
          <el-table-column label="状态" width="88" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.status === 'success' ? 'success' : 'danger'" effect="plain">
                {{ scope.row.status === 'success' ? '成功' : '失败' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="message" label="明细" min-width="280" show-overflow-tooltip />
        </el-table>
        <div v-else class="result-empty">暂无明细日志</div>
        <div class="preview-actions">
          <el-button size="small" type="primary" plain @click="resetImport">继续导入</el-button>
        </div>
      </div>
    </div>

    <!-- 导出 -->
    <div v-show="activeTab === 'export'" class="pane">
      <div class="export-card">
        <div class="export-head">
          <i class="el-icon-files" />
          <div>
            <div class="export-title">导出入住明细</div>
            <p class="export-desc">导出 Excel，字段与导入模板兼容，改完可再上传同步</p>
          </div>
        </div>
        <ul class="export-list">
          <li>范围：当前学校全部在宿人员（可限定左侧选中楼栋）</li>
          <li>字段：学号、姓名、性别、班级、宿舍楼、房间号、床位号、入住时间</li>
        </ul>
        <el-checkbox v-model="exportCurrentBuildingOnly" :disabled="!buildingId">
          仅导出当前选中楼栋<span v-if="buildingName">（{{ buildingName }}）</span>
        </el-checkbox>
        <div class="export-action">
          <el-button type="primary" size="small" icon="el-icon-download" :loading="exporting" @click="doExport">
            导出 Excel
          </el-button>
        </div>
      </div>
    </div>

    <div slot="footer">
      <el-button size="small" @click="dialogVisible = false">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { read, utils, writeFile } from 'xlsx'
import { exportLiveOccupancy, importLiveOccupancy } from '../../../api/modules/dormitory'

export default {
  name: 'DormLiveBatchConsoleDialog',
  props: {
    visible: { type: Boolean, default: false },
    schoolDepartmentId: { type: [Number, String], default: 0 },
    buildingId: { type: [Number, String], default: 0 },
    buildingName: { type: String, default: '' }
  },
  data() {
    return {
      dialogVisible: false,
      activeTab: 'import',
      importStep: 0,
      importSteps: ['上传解析', '预览核对', '导入结果'],
      parsing: false,
      importing: false,
      exporting: false,
      previewRows: [],
      importResult: { SuccessCount: 0, FailCount: 0, Logs: [] },
      resultKeyword: '',
      resultStatusFilter: '',
      exportCurrentBuildingOnly: true
    }
  },
  computed: {
    importResultTitle() {
      const s = this.importResult.SuccessCount || 0
      const f = this.importResult.FailCount || 0
      return `导入完成：成功 ${s}，失败/跳过 ${f}`
    },
    importResultSub() {
      if (!this.importResult.FailCount) return '全部处理成功，可关闭后刷新房间列表查看'
      return '请根据下方明细核对失败原因后重试'
    },
    importLogRows() {
      return this.parseImportLogs(this.importResult.Logs)
    },
    importSuccessCount() {
      return this.importLogRows.filter(r => r.status === 'success').length
    },
    importFailCount() {
      return this.importLogRows.filter(r => r.status === 'fail').length
    },
    filteredImportLogRows() {
      const kw = (this.resultKeyword || '').trim().toLowerCase()
      const st = this.resultStatusFilter
      return this.importLogRows.filter(r => {
        if (st && r.status !== st) return false
        if (!kw) return true
        const blob = `${r.rowNo || ''}${r.message || ''}${r.raw || ''}`.toLowerCase()
        return blob.indexOf(kw) >= 0
      })
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(v) {
        this.dialogVisible = v
        if (v) {
          this.exportCurrentBuildingOnly = !!this.buildingId
        }
      }
    }
  },
  methods: {
    handleClose() {
      this.$emit('update:visible', false)
    },
    resetImport() {
      this.importStep = 0
      this.previewRows = []
      this.importResult = { SuccessCount: 0, FailCount: 0, Logs: [] }
      this.resultKeyword = ''
      this.resultStatusFilter = ''
    },
    /**
     * 将后端字符串日志解析为表格行
     */
    parseImportLogs(logs) {
      return (logs || []).map((line, i) => {
        const raw = String(line || '').trim()
        const m = raw.match(/^第(\d+)行[：:]\s*(.*)$/)
        const message = m ? m[2] : raw
        const ok = /成功\s*$/.test(message) || (/成功/.test(message) && !/失败/.test(message))
        return {
          index: i + 1,
          rowNo: m ? m[1] : String(i + 1),
          message,
          status: ok ? 'success' : 'fail',
          raw
        }
      })
    },
    downloadTemplate() {
      const sampleBuilding = this.buildingName || '一号楼'
      const rows = [
        { 学号: '20260101', 姓名: '张三', 宿舍楼: sampleBuilding, 房间号: '101', 床位号: 1 },
        { 学号: '20260102', 姓名: '李四', 宿舍楼: sampleBuilding, 房间号: '101', 床位号: 2 }
      ]
      const sheet = utils.json_to_sheet(rows)
      const book = utils.book_new()
      utils.book_append_sheet(book, sheet, '入住导入')
      writeFile(book, '宿舍入住批量导入模板.xlsx')
    },
    onFileChange(file) {
      const raw = file && file.raw
      if (!raw) return
      if (!/\.(xls|xlsx)$/i.test(raw.name || '')) {
        this.$message.error('请上传 .xls 或 .xlsx 文件')
        return
      }
      this.parsing = true
      const reader = new FileReader()
      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target.result)
          const wb = read(data, { type: 'array' })
          const sheet = wb.Sheets[wb.SheetNames[0]]
          const json = utils.sheet_to_json(sheet, { defval: '' })
          const rows = (json || []).map(r => this.mapRow(r)).filter(r => r.StuCode || r.StuName)
          if (!rows.length) {
            this.$message.warning('未解析到有效数据，请检查模板列名')
            return
          }
          this.previewRows = rows
          this.importStep = 1
        } catch (err) {
          this.$message.error('文件解析失败')
        } finally {
          this.parsing = false
        }
      }
      reader.onerror = () => {
        this.parsing = false
        this.$message.error('文件读取失败')
      }
      reader.readAsArrayBuffer(raw)
    },
    mapRow(row) {
      const pick = (...keys) => {
        for (const k of keys) {
          if (row[k] != null && String(row[k]).trim() !== '') return String(row[k]).trim()
        }
        return ''
      }
      const buildingName = pick('宿舍楼', '楼栋', 'BuildingName')
      const roomCode = pick('房间号', '房间', 'RoomCode', 'RoomName')
      const bedRaw = pick('床位号', '床位', 'BedNumber', '床号')
      const bedNumber = parseInt(String(bedRaw).replace(/[^\d]/g, ''), 10)
      return {
        StuCode: pick('学号', 'StuCode', '学工号'),
        StuName: pick('姓名', 'StuName', '名字'),
        BuildingName: buildingName,
        RoomCode: roomCode,
        BedNumber: Number.isFinite(bedNumber) ? bedNumber : 0
      }
    },
    removePreviewRow(index) {
      this.previewRows.splice(index, 1)
    },
    async confirmImport() {
      if (!this.schoolDepartmentId) return this.$message.warning('请先选择学校')
      if (!this.previewRows.length) return this.$message.warning('没有可导入的数据')
      this.importing = true
      try {
        const res = await importLiveOccupancy(this.previewRows, {
          departmentId: this.schoolDepartmentId
        })
        if (res.data && res.data.success) {
          this.importResult = res.data.response || { SuccessCount: 0, FailCount: 0, Logs: [] }
          this.resultKeyword = ''
          this.resultStatusFilter = ''
          this.importStep = 2
          this.$emit('changed')
          this.$message.success(res.data.msg || '导入完成')
        } else {
          this.$message.error((res.data && res.data.msg) || '导入失败')
        }
      } catch (e) {
        this.$message.error('导入请求失败')
      } finally {
        this.importing = false
      }
    },
    async doExport() {
      if (!this.schoolDepartmentId) return this.$message.warning('请先选择学校')
      this.exporting = true
      try {
        const params = { departmentId: this.schoolDepartmentId }
        if (this.exportCurrentBuildingOnly && this.buildingId) {
          params.buildingId = this.buildingId
        }
        const res = await exportLiveOccupancy(params)
        if (!(res.data && res.data.success)) {
          this.$message.error((res.data && res.data.msg) || '导出失败')
          return
        }
        const list = res.data.response || []
        if (!list.length) {
          this.$message.info('暂无入住数据可导出')
          return
        }
        const rows = list.map(x => ({
          学号: x.StuCode || '',
          姓名: x.StuName || '',
          性别: x.GenderText || '',
          班级: x.ClassName || '',
          宿舍楼: x.BuildingName || '',
          房间号: x.RoomCode || '',
          床位号: x.BedNumber,
          入住时间: x.CheckInTime || ''
        }))
        const sheet = utils.json_to_sheet(rows)
        const book = utils.book_new()
        utils.book_append_sheet(book, sheet, '入住明细')
        const suffix = this.exportCurrentBuildingOnly && this.buildingName
          ? `_${this.buildingName}`
          : ''
        writeFile(book, `宿舍入住明细${suffix}.xlsx`)
        this.$message.success(`已导出 ${rows.length} 条`)
      } catch (e) {
        this.$message.error('导出请求失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.pane {
  min-height: 300px;
}
.batch-tabs {
  margin-top: -6px;
}
.step-nav {
  display: flex;
  gap: 8px;
  margin: 4px 0 16px;
  padding: 6px;
  border-radius: 10px;
  background: #f0fdfa;
  border: 1px solid #ccfbf1;
}
.step-nav-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 6px;
  border-radius: 8px;
  color: #64748b;
  font-size: 13px;
  transition: background .15s ease, color .15s ease;
}
.step-nav-item .step-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: #e2e8f0;
  color: #64748b;
}
.step-nav-item.active {
  background: #fff;
  color: #0f766e;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.step-nav-item.active .step-num {
  background: #0d9488;
  color: #fff;
}
.step-nav-item.done {
  color: #0f766e;
}
.step-nav-item.done .step-num {
  background: #99f6e4;
  color: #0f766e;
}
.flow {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.flow-card {
  display: flex;
  gap: 14px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
}
.flow-card.primary {
  border-color: #99f6e4;
  background: linear-gradient(180deg, #f0fdfa 0%, #fff 70%);
}
.flow-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #0d9488;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.flow-card.primary .flow-icon {
  background: #ccfbf1;
}
.flow-title {
  font-size: 14px;
  font-weight: 700;
  color: #134e4a;
  margin-bottom: 4px;
}
.flow-desc {
  margin: 0 0 10px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
.preview-meta {
  font-size: 13px;
  color: #475569;
}
.preview-meta b {
  color: #0f766e;
}
.preview-actions {
  margin-top: 12px;
  text-align: right;
}
.result-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.result-empty {
  padding: 28px 12px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
}
.danger {
  color: #e11d48 !important;
}
.export-card {
  border-radius: 12px;
  padding: 18px;
  background: linear-gradient(180deg, #f0fdfa 0%, #fff 55%);
  border: 1px solid #99f6e4;
}
.export-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
}
.export-head > i {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #ccfbf1;
  color: #0d9488;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.export-title {
  font-weight: 700;
  color: #134e4a;
  font-size: 15px;
}
.export-desc {
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
.export-list {
  margin: 0 0 14px;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 1.7;
}
.export-action {
  margin-top: 16px;
}
.result-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
}
.result-banner.is-ok {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #047857;
}
.result-banner.is-warn {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #b45309;
}
.result-banner i {
  font-size: 26px;
  margin-top: 2px;
}
.result-title {
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 4px;
}
.result-sub {
  font-size: 13px;
  opacity: 0.9;
}
</style>

<style>
.dorm-live-batch-dialog {
  border-radius: 12px;
  overflow: hidden;
}
.dorm-live-batch-dialog .el-dialog__header {
  padding: 14px 18px 8px;
}
.dorm-live-batch-dialog .el-dialog__title {
  font-weight: 700;
  color: #134e4a;
  font-size: 16px;
}
.dorm-live-batch-dialog .el-dialog__body {
  padding: 4px 18px 8px;
}
.dorm-live-batch-dialog .el-dialog__footer {
  padding: 10px 18px 14px;
}
.dorm-live-batch-dialog .el-tabs__item.is-active {
  color: #0f766e;
}
.dorm-live-batch-dialog .el-tabs__active-bar {
  background-color: #0d9488;
}
.dorm-live-batch-dialog .el-button--primary {
  background-color: #0d9488;
  border-color: #0d9488;
}
.dorm-live-batch-dialog .el-button--primary:hover,
.dorm-live-batch-dialog .el-button--primary:focus {
  background-color: #0f766e;
  border-color: #0f766e;
}
</style>
